import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Search } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCondicoesPagamento, useFormasPagamentoCombo } from '@/features/financeiro/hooks/useCadastrosFinanceiros';
import { CondicoesPagamentoTable } from '@/features/financeiro/components/CondicoesPagamentoTable';
import { CondicaoPagamentoDialog } from '@/features/financeiro/components/CondicaoPagamentoDialog';
import { cadastrosFinanceirosService } from '@/features/financeiro/services/cadastrosFinanceirosService';
import type { CondicaoPagamento, CondicaoPagamentoDetalhe, SalvarCondicaoPagamentoRequest } from '@/features/financeiro/types';
import { usePaginacaoLocal } from '@/hooks/usePaginacaoLocal';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';

const CondicoesPagamento = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCondicao, setEditingCondicao] = useState<CondicaoPagamentoDetalhe | null>(null);
  const [busca, setBusca] = useState('');
  const [formaPagRecId, setFormaPagRecId] = useState<number | undefined>();

  const { formasPagamento } = useFormasPagamentoCombo(true);
  const { response, isLoading, criar, atualizar, bloquear, isCriando, isAtualizando } = useCondicoesPagamento(formaPagRecId, busca);
  const condicoes = response?.itens ?? [];
  const paginacao = usePaginacaoLocal(condicoes);

  const handleOpenDialog = async (condicao?: CondicaoPagamento) => {
    if (condicao?.id) {
      const detalhe = await cadastrosFinanceirosService.getCondicaoPagamentoById(condicao.id);
      setEditingCondicao(detalhe);
    } else {
      setEditingCondicao(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCondicao(null);
  };

  const handleSubmit = (data: SalvarCondicaoPagamentoRequest) => {
    if (editingCondicao) {
      atualizar({ id: editingCondicao.id, req: data }, { onSuccess: handleCloseDialog });
    } else {
      criar(data, { onSuccess: handleCloseDialog });
    }
  };

  const handleEdit = (condicao: CondicaoPagamento) => handleOpenDialog(condicao);

  if (isLoading) {
    return <div className="flex items-center justify-center h-96"><p className="text-muted-foreground">Carregando...</p></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Condições de Pagamento</h1>
          <p className="text-muted-foreground">Gerencie as condições de pagamento e parcelamentos</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />Nova Condição
        </Button>
      </div>

      <div className="flex gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar condição..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={formaPagRecId?.toString() ?? 'all'}
          onValueChange={(v) => setFormaPagRecId(v === 'all' ? undefined : Number(v))}
        >
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Filtrar por forma de pagamento" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as formas</SelectItem>
            {formasPagamento.map((f) => (
              <SelectItem key={f.id} value={f.id.toString()}>{f.descricao}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <CondicoesPagamentoTable
        condicoes={paginacao.itensPaginados}
        onEdit={handleOpenDialog}
        onBloquear={(id) => bloquear(id)}
      />

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <CondicaoPagamentoDialog
        open={dialogOpen}
        onOpenChange={handleCloseDialog}
        onSubmit={handleSubmit}
        editingCondicao={editingCondicao}
        formaPagRecIdFiltro={formaPagRecId}
        isLoading={isCriando || isAtualizando}
      />
    </div>
  );
};

export default CondicoesPagamento;
